import React from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@mantine/core';

import { mfaMessages } from '@shipstation/client-common/mfa/mfa-messages';

import {
  NotificationService,
  NOTIFICATION_TYPE_ERROR,
  NOTIFICATION_TYPE_INFO,
  NOTIFICATION_TYPE_SUCCESS,
  NOTIFICATION_DURATIONS,
} from 'ui-components/services/notification-service';

import type { MfaNotifier } from '@shipstation/client-common/mfa/mfa-platform';
import type { MfaTone } from '@shipstation/client-common/mfa/mfa-types';
import type { NotificationType } from 'ui-components/services/notification-service';

const TONE_TO_NOTIFICATION_TYPE: Record<MfaTone, NotificationType> = {
  info: NOTIFICATION_TYPE_INFO,
  success: NOTIFICATION_TYPE_SUCCESS,
  error: NOTIFICATION_TYPE_ERROR,
};

const DEFAULT_DURATION_MS_BY_TONE: Record<MfaTone, number | undefined> = {
  info: undefined,
  success: NOTIFICATION_DURATIONS.SHORT,
  error: NOTIFICATION_DURATIONS.MEDIUM,
};

type ToneNotificationOptions = {
  message: React.ReactNode;
  tone: MfaTone;
  durationMs?: number;
};

const showToneNotification = ({ message, tone, durationMs }: ToneNotificationOptions): string => {
  return NotificationService.showNotification({
    title: '',
    notificationType: TONE_TO_NOTIFICATION_TYPE[tone],
    content: message,
    duration: durationMs,
  });
};

const updateToneNotification = (notificationId: string, { message, tone, durationMs }: ToneNotificationOptions): void => {
  NotificationService.updateNotification(notificationId, {
    title: '',
    notificationType: TONE_TO_NOTIFICATION_TYPE[tone],
    content: message,
    duration: durationMs,
  });
};

/**
 * ss-ui-client implementation of the client-common `MfaNotifier` seam.
 * Injected via `configureMfaNotifier` so client-common never learns about
 * `NotificationService` and never needs `@mantine/notifications`.
 */
export const ssUiClientMfaNotifier: MfaNotifier = {
  show: ({ message, tone }) => {
    return showToneNotification({ message, tone, durationMs: DEFAULT_DURATION_MS_BY_TONE[tone] });
  },
  update: (notificationId, { message, tone }) => {
    updateToneNotification(notificationId, { message, tone, durationMs: DEFAULT_DURATION_MS_BY_TONE[tone] });
  },
};

export const showMfaNeededNotification = (): string => {
  return showToneNotification({
    message: <FormattedMessage {...mfaMessages.mfaIsNeededNotification} />,
    tone: 'error',
    durationMs: NOTIFICATION_DURATIONS.MEDIUM,
  });
};

export const showVerifyingInProgressNotification = (): string => {
  return showToneNotification({
    message: <FormattedMessage {...mfaMessages.mfaVerificationInProgress} />,
    tone: 'info',
  });
};

export const showVerifyingSuccess = (notificationId: string): void => {
  updateToneNotification(notificationId, {
    message: <FormattedMessage {...mfaMessages.mfaVerificationSuccess} />,
    tone: 'success',
    durationMs: NOTIFICATION_DURATIONS.SHORT,
  });
};

/**
 * Keeps ss-ui-client's retry affordance: the `<click>` chunk renders as a link-styled
 * button that re-runs the token refresh. shipstation-lite dropped this in favour of
 * "Please try again." — ss-ui-client's behaviour wins here.
 */
export const showVerifyingFailed = (notificationId: string, onRetry: () => void): void => {
  updateToneNotification(notificationId, {
    message: (
      <FormattedMessage
        {...mfaMessages.mfaVerificationFailure}
        values={{
          click: (chunks) => {
            return (
              <Button onClick={onRetry} variant='hyperlink'>
                {chunks}
              </Button>
            );
          },
        }}
      />
    ),
    tone: 'error',
  });
};
